/**
 * A locale segment is a language (`en`) or a language-region tag (`en-US`).
 * Shop slugs such as `from-qr` must not match.
 */
const LOCALE_SEGMENT = /^[a-z]{2}(?:-[a-z]{2})?$/i;

/** Used only when the marketplace has not published a locale list yet. */
const UNLOADED_MARKETPLACE_LOCALES = ['fr', 'es'] as const;

export interface MarketplaceLocaleSource {
  locales?: ReadonlyArray<{ locale?: string | null }> | null;
  default_locale?: { locale?: string | null } | null;
}

export interface MarketplaceLocaleContext {
  supportedLocales: readonly string[];
  defaultLocale: string;
  /** Already stored app locale. Used only when the URL has no locale prefix. */
  currentStoreLocale?: string;
  /** `navigator.language` primary subtag. Used only when the URL has no locale prefix. */
  browserLang?: string;
}

export type MarketplaceLocaleDecision =
  | { action: 'keep'; locale: string }
  | {
      action: 'redirect';
      pathname: string;
      locale: string;
      reason: 'unsupported' | 'canonicalize' | 'missing';
    };

export function buildMarketplaceLocaleContext(
  marketplace: MarketplaceLocaleSource,
  extras?: Pick<MarketplaceLocaleContext, 'currentStoreLocale' | 'browserLang'>,
): MarketplaceLocaleContext {
  const fromMarketplace = (marketplace.locales ?? [])
    .map(locale => locale.locale?.trim() ?? '')
    .filter(locale => locale.length > 0);

  return {
    supportedLocales: fromMarketplace.length ? fromMarketplace : [...UNLOADED_MARKETPLACE_LOCALES],
    defaultLocale: marketplace.default_locale?.locale?.trim() || 'fr',
    currentStoreLocale: extras?.currentStoreLocale,
    browserLang: extras?.browserLang,
  };
}

/**
 * Decides whether the current path may keep its locale prefix.
 * An unknown language is replaced by the marketplace default, not by the browser language.
 * A missing prefix still prefers an allowed store locale, then an allowed browser language.
 */
export function decideMarketplaceLocale(
  pathname: string,
  context: MarketplaceLocaleContext,
): MarketplaceLocaleDecision {
  const allowList = effectiveAllowList(context);
  const defaultLocale = canonicalDefaultLocale(context, allowList);
  const segments = pathname.split('/').filter(segment => segment.length > 0);
  const first = segments[0] ?? '';

  if (first && isLocaleSegment(first)) {
    const canonical = findSupportedLocale(first, allowList);
    if (!canonical) {
      return {
        action: 'redirect',
        pathname: joinPath(replaceFirst(segments, defaultLocale)),
        locale: defaultLocale,
        reason: 'unsupported',
      };
    }
    if (first !== canonical) {
      return {
        action: 'redirect',
        pathname: joinPath(replaceFirst(segments, canonical)),
        locale: canonical,
        reason: 'canonicalize',
      };
    }
    return { action: 'keep', locale: canonical };
  }

  const chosen = chooseLocaleWhenMissing(allowList, defaultLocale, context);
  return {
    action: 'redirect',
    pathname: joinPath([chosen, ...segments]),
    locale: chosen,
    reason: 'missing',
  };
}

/**
 * Full URL to load instead of `href`, or `null` when the URL is already allowed.
 * Search and hash are preserved so a QR `onSiteLocationId` survives the language correction.
 */
export function marketplaceLocaleRedirectUrl(
  href: string,
  context: MarketplaceLocaleContext,
): string | null {
  const current = new URL(href, 'https://placeholder.local');
  const decision = decideMarketplaceLocale(current.pathname, context);
  if (decision.action === 'keep') {
    return null;
  }

  const next = new URL(current.href);
  next.pathname = decision.pathname;
  if (
    next.pathname === current.pathname &&
    next.search === current.search &&
    next.hash === current.hash
  ) {
    return null;
  }
  return next.toString();
}

/** Maps a requested locale onto the marketplace allow-list, or the default locale. */
export function resolveAllowedMarketplaceLocale(
  locale: string,
  context: MarketplaceLocaleContext,
): string {
  const allowList = effectiveAllowList(context);
  return findSupportedLocale(locale, allowList) ?? canonicalDefaultLocale(context, allowList);
}

function effectiveAllowList(context: MarketplaceLocaleContext): string[] {
  const supported = context.supportedLocales.map(locale => locale.trim()).filter(Boolean);
  if (supported.length) {
    return supported;
  }
  return [...UNLOADED_MARKETPLACE_LOCALES];
}

function canonicalDefaultLocale(
  context: MarketplaceLocaleContext,
  allowList: readonly string[],
): string {
  const requested = context.defaultLocale.trim();
  return (findSupportedLocale(requested, allowList) ?? requested) || allowList[0] || 'fr';
}

function chooseLocaleWhenMissing(
  allowList: readonly string[],
  defaultLocale: string,
  context: MarketplaceLocaleContext,
): string {
  const fromStore = findSupportedLocale(context.currentStoreLocale ?? '', allowList);
  if (fromStore) {
    return fromStore;
  }
  const fromBrowser = findSupportedLocale(context.browserLang ?? '', allowList);
  if (fromBrowser) {
    return fromBrowser;
  }
  return defaultLocale;
}

function findSupportedLocale(candidate: string, allowList: readonly string[]): string | undefined {
  const folded = candidate.trim().toLowerCase();
  if (!folded) {
    return undefined;
  }
  return allowList.find(locale => locale.toLowerCase() === folded);
}

function isLocaleSegment(segment: string): boolean {
  return LOCALE_SEGMENT.test(segment);
}

function replaceFirst(segments: string[], locale: string): string[] {
  return [locale, ...segments.slice(1)];
}

function joinPath(segments: readonly string[]): string {
  return `/${segments.join('/')}`;
}
